import type { StationsFeature } from '@ev/shared'
import { type PowerBand, siteBandFor } from '../theme/palette'
import { KEYBOARD_LIST_LIMIT, LOCATION_VIEW } from './config'

/**
 * What is on screen, and what a keyboard can reach.
 *
 * Both answers are pure functions of the feature list and the camera, so they
 * are tested without a WebGL context: the map hands over its bounds and centre
 * and gets back the visible count and the keyboard list.
 */

export interface ViewportBounds {
  readonly west: number
  readonly south: number
  readonly east: number
  readonly north: number
}

/**
 * Longitude may arrive unwrapped once the visitor has panned across the
 * antimeridian, so it is compared modulo 360 rather than as a plain range.
 */
const withinLongitude = (lon: number, west: number, east: number): boolean => {
  if (east - west >= 360) return true
  const offset = (((lon - west) % 360) + 360) % 360
  return offset <= (((east - west) % 360) + 360) % 360
}

export const isInBounds = (
  [lon, lat]: readonly [number, number],
  bounds: ViewportBounds,
): boolean =>
  lat >= bounds.south && lat <= bounds.north && withinLongitude(lon, bounds.west, bounds.east)

export const countInBounds = (
  features: readonly StationsFeature[],
  bounds: ViewportBounds,
): number => {
  let count = 0
  features.forEach((feature) => {
    if (isInBounds(feature.geometry.coordinates, bounds)) count += 1
  })
  return count
}

/** One entry of the keyboard list. Flat, because it is rendered straight into a listbox. */
export interface KeyboardTarget {
  readonly id: string
  readonly name: string
  readonly city: string
  readonly ac: number
  readonly dc: number
  readonly pmax: number
  readonly band: PowerBand
  readonly lon: number
  readonly lat: number
}

const toTarget = (feature: StationsFeature): KeyboardTarget => {
  const { id, n, c, ac, dc, pmax } = feature.properties
  const [lon, lat] = feature.geometry.coordinates
  return { id, name: n, city: c, ac, dc, pmax, band: siteBandFor({ ac, dc, pmax }), lon, lat }
}

/**
 * Rough squared distance in degrees, with longitude scaled by latitude so a
 * degree east counts for what it is worth this far north. Good enough to
 * order a list; nobody is navigating by it.
 */
const distanceRank = (lon: number, lat: number, center: readonly [number, number]): number => {
  const scale = Math.cos((lat * Math.PI) / 180)
  const dx = (lon - center[0]) * scale
  const dy = lat - center[1]
  return dx * dx + dy * dy
}

/**
 * How far round the globe the shorter way is, in degrees, signed.
 *
 * A fix near the antimeridian would otherwise read as nearly 360 degrees from a
 * charger on the other side of it, and the frame built from that would be the
 * whole planet.
 */
const longitudeDelta = (lon: number, from: number): number =>
  ((((lon - from) % 360) + 540) % 360) - 180

/** Web Mercator cannot draw the poles, so the frame never asks it to. */
const MAX_LATITUDE = 85

/** Below this the longitude scaling blows up; nothing in Poland comes near it. */
const MIN_LONGITUDE_SCALE = 0.05

export type CameraBox = readonly [readonly [number, number], readonly [number, number]]

/**
 * A frame centred on the visitor that holds the nearest `minSites` sites.
 *
 * Symmetric on the fix on purpose: the point of this view is that the blue dot
 * is in the middle of the screen, so the box grows on both sides rather than
 * towards wherever the chargers happen to be. The floor keeps a visitor
 * standing in a car park full of chargers from being zoomed into the tarmac;
 * `fitBounds` caps the other end with {@link LOCATION_VIEW.maxZoom}.
 *
 * Pure, so it is tested without a WebGL context: the camera is derived from the
 * feature list and the fix, and the map only has to fly to the answer. Passing
 * an empty list is not an error - it returns the floor, which is the right
 * frame for a visitor in a part of the country this filter reaches nothing in.
 */
export const locationViewBounds = (
  features: readonly StationsFeature[],
  center: readonly [number, number],
  minSites: number = LOCATION_VIEW.minSites,
  minHalfSpanDeg: number = LOCATION_VIEW.minHalfSpanDeg,
): CameraBox => {
  const [centerLon, centerLat] = center

  const ranked = features.map((feature) => {
    const [lon, lat] = feature.geometry.coordinates
    return { lon, lat, rank: distanceRank(lon, lat, center) }
  })
  ranked.sort((a, b) => a.rank - b.rank)

  // Longitude is widened by latitude so the floor is square on screen rather
  // than a letterbox this far north.
  const scale = Math.max(Math.cos((centerLat * Math.PI) / 180), MIN_LONGITUDE_SCALE)
  let latHalf = minHalfSpanDeg
  let lonHalf = minHalfSpanDeg / scale

  ranked.slice(0, Math.max(0, minSites)).forEach(({ lon, lat }) => {
    latHalf = Math.max(latHalf, Math.abs(lat - centerLat))
    lonHalf = Math.max(lonHalf, Math.abs(longitudeDelta(lon, centerLon)))
  })

  const cappedLon = Math.min(lonHalf, 180)
  return [
    [centerLon - cappedLon, Math.max(centerLat - latHalf, -MAX_LATITUDE)],
    [centerLon + cappedLon, Math.min(centerLat + latHalf, MAX_LATITUDE)],
  ]
}

/**
 * The sites a keyboard user can step through: everything matching and in view,
 * nearest to the middle of the screen first, capped so the listbox stays a
 * list rather than a scrollable copy of the whole country.
 */
export const keyboardTargets = (
  features: readonly StationsFeature[],
  bounds: ViewportBounds,
  center: readonly [number, number],
  limit: number = KEYBOARD_LIST_LIMIT,
): KeyboardTarget[] => {
  const ranked: { target: KeyboardTarget; rank: number }[] = []
  features.forEach((feature) => {
    if (!isInBounds(feature.geometry.coordinates, bounds)) return
    const [lon, lat] = feature.geometry.coordinates
    ranked.push({ target: toTarget(feature), rank: distanceRank(lon, lat, center) })
  })

  ranked.sort((a, b) => a.rank - b.rank || a.target.id.localeCompare(b.target.id))
  return ranked.slice(0, limit).map((entry) => entry.target)
}
